import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import { newOfferSubscription } from "../FindOffers";
import { Offer, SearchOffersByCategoryQueryQuery, SearchOffersByCategoryQueryQueryVariables } from "../../../schemaTypes";

export const searchOffersByCategoryQuery = gql`
  query SearchOffersByCategoryQuery(
    $input: SearchOffersByCategoryInput
    $offset: Int!
    $limit: Int!
  ) {
    searchOffersByCategory(
      input: $input, 
      offset: $offset, 
      limit: $limit
    ) {
      id
      title
      description
      bannerUrl
      category {
        id
        name
        imgSrc
        createdBy{
          id
          role
        }
        createdAt
        updatedAt
      }
      offerType
      discountType
      discountValue
      partnerVoucher
      referralUrl
      status
      usageLimitPerUser
      totalUsageLimit
      startDate
      endDate
      community{
        id
        name
        alias
      }
      location{
        id
        city
        district
        streetName
        country
        postalCode
        landmark
        latitude
        longitude
        createdAt
      }
      redemptions
      revenueGenerated
      clickThroughs
      featured
      createdBy{
        id
        role
        admin {
          id
          shortId
          firstname
          lastname
          email
          phone
          merchantProfile {
            id
            businessName
            businessType
            regCountry
            businessRegNumber
            taxId
            address
            addressAlt
            city
            country
            postcode
            companyName
            username
            industry {
              id
              name
              createdBy{
                id
                role
              }
              createdAt
              updatedAt
            }
            companyLocation
            logoUrl
            bannerUrl
            description
            commissionRate
            createdAt
            updatedAt
          }
          createdAt
          updatedAt
        }
      }
      # njoyCode {
      #   id
      #   code
      #   redeemedAt
      #   createdAt
      #   updatedAt
      # }
      location{
        id
        district
        city
        streetName
        country
        postalCode
        landmark
        latitude
        longitude
      }
      createdAt
      updatedAt
    }
  }
`;

newOfferSubscription;

export interface WithSearchOffersByCategory {
  offers: Offer[];
  loading: boolean;
  loadMore: () => void;
  hasMoreOffers: boolean;
  subscribe: () => void;
}

interface Props {
  variables:   SearchOffersByCategoryQueryQueryVariables;
  children: (data: WithSearchOffersByCategory) => React.ReactElement | null;
}

export const SearchOffersByCategory: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore, subscribeToMore } = useQuery<
    SearchOffersByCategoryQueryQuery,
    SearchOffersByCategoryQueryQueryVariables
  >(searchOffersByCategoryQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  let offers: Offer[] = [];
  if (data && data.searchOffersByCategory) {
    offers = data.searchOffersByCategory as Offer[];
  }

  let hasMoreOffers = offers.length % variables.limit === 0;
  if (offers.length <= variables.offset) {
    hasMoreOffers = false;
  }

  const loadMore = useCallback(() => {
    fetchMore({
      variables: {
        ...variables,
        offset: offers.length
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          ...prev,
          searchOffersByCategory: [
            ...prev.searchOffersByCategory,
            ...fetchMoreResult.searchOffersByCategory
          ]
        };
      }
    });
  }, [fetchMore, variables, offers.length]);

  // Subscription for new offers
  const subscribe = useCallback(() => {
    subscribeToMore({
      document: newOfferSubscription,
      variables: { variables },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) {
          return prev;
        }
        return {
          ...prev,
          searchOffersByCategory: [
            ...prev.searchOffersByCategory,
            (subscriptionData.data as any).newOffer
          ]
        };
      }
    });
  }, [subscribeToMore, variables]);

  return children({
    hasMoreOffers,
    offers,
    loading,
    loadMore,
    subscribe,
  });
};